import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ApiError, type ApiScope } from "@/lib/apiClient";
import { queryKeys } from "@/lib/queryKeys";
import { useSessionStore } from "@/stores/sessionStore";
import { API_ERROR_CODES } from "@/types/api";
import * as authApi from "./api";
import { authCopy } from "./copy";
import type { LoginInput } from "./schemas";

/**
 * Confirma no servidor se ha sessao.
 *
 * O store persistido serve para nao piscar a tela de login num F5; quem diz
 * se a sessao vale e este `/me`. `retry: false` porque 401 aqui e uma
 * resposta legitima ("nao esta logado"), nao uma falha a repetir.
 */
export function useSessionQuery(scope: ApiScope = "user") {
  const setSession = useSessionStore((s) => s.setSession);
  const markAnonymous = useSessionStore((s) => s.markAnonymous);

  return useQuery({
    queryKey: queryKeys.session(scope),
    queryFn: async () => {
      try {
        const session = await authApi.fetchSession(scope);
        setSession(session);
        return session;
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthenticated) {
          markAnonymous();
          return null;
        }
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useLogin(scope: ApiScope = "user") {
  const queryClient = useQueryClient();
  const setSession = useSessionStore((s) => s.setSession);

  return useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input, scope),
    onSuccess: (session) => {
      setSession(session);
      queryClient.setQueryData(queryKeys.session(scope), session);
    },
  });
}

export function useLogout(scope: ApiScope = "user") {
  const queryClient = useQueryClient();
  const clearSession = useSessionStore((s) => s.clearSession);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => authApi.logout(scope),
    // `onSettled` e nao `onSuccess`: se a chamada falhar, o usuario clicou
    // em "sair" e precisa sair. O cookie ja pode estar invalido; insistir em
    // manter a sessao na UI porque a rede caiu e pior que limpar.
    onSettled: () => {
      clearSession();
      queryClient.clear();
      navigate(scope === "operator" ? "/admin/entrar" : "/entrar", { replace: true });
    },
  });
}

/** Aceite do termo de consentimento LGPD (RF-02.6). Atualiza a sessao local
 *  na hora — sem isso o gate continuaria bloqueando ate o proximo /me. */
export function useAcceptConsent() {
  const queryClient = useQueryClient();
  const user = useSessionStore((s) => s.user);
  const setSession = useSessionStore((s) => s.setSession);

  return useMutation({
    mutationFn: () => authApi.acceptConsent(),
    onSuccess: ({ consent_given }) => {
      if (!user) return;

      const updatedUser = { ...user, consent_given };
      setSession({ user: updatedUser });
      queryClient.setQueryData(queryKeys.session("user"), (previous: unknown) =>
        previous && typeof previous === "object" ? { ...previous, user: updatedUser } : previous,
      );
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) => authApi.requestPasswordReset(email),
  });
}

export function useFindTenant() {
  return useMutation({
    mutationFn: (email: string) => authApi.findTenant(email),
  });
}

export function useConfirmPasswordReset() {
  const setSession = useSessionStore((s) => s.setSession);

  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.confirmPasswordReset(token, password),
    onSuccess: (session) => setSession(session),
  });
}

/** Traduz ApiError na mensagem que o usuario le. */
export function authErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return authCopy.errors.generic;

  switch (error.code) {
    case API_ERROR_CODES.invalidCredentials:
      return authCopy.errors.invalidCredentials;
    case API_ERROR_CODES.tenantRequired:
      return authCopy.errors.tenantRequired;
    case API_ERROR_CODES.tenantSuspended:
      return authCopy.errors.tenantSuspended;
    case API_ERROR_CODES.authUnavailable:
      return authCopy.errors.authUnavailable;
    case API_ERROR_CODES.network:
      return authCopy.errors.network;
    case API_ERROR_CODES.rateLimited:
      return authCopy.errors.rateLimited(error.retryAfter ?? 60);
    default:
      return error.message || authCopy.errors.generic;
  }
}
